import { useEffect, useMemo, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { isTauri } from "../lib/tauri";
import { Icon } from "./icons";

type NavId = "generate" | "samples" | "library" | "admin";

interface NavItem {
  id: NavId;
  label: string;
  kbd: string;
  to: string;
  matches: (path: string) => boolean;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  group: "workspace" | "system";
}

const NAV: NavItem[] = [
  {
    id: "generate",
    label: "Generate",
    kbd: "G",
    to: "/generate",
    matches: (p) => p.startsWith("/generate"),
    icon: Icon.Generate,
    group: "workspace",
  },
  {
    id: "samples",
    label: "Samples",
    kbd: "S",
    to: "/jobs",
    matches: (p) => p.startsWith("/jobs") || p.startsWith("/samples"),
    icon: Icon.Music,
    group: "workspace",
  },
  {
    id: "library",
    label: "Voice Library",
    kbd: "L",
    to: "/library",
    matches: (p) => p.startsWith("/library"),
    icon: Icon.Library,
    group: "workspace",
  },
  {
    id: "admin",
    label: "Admin",
    kbd: "A",
    to: "/admin",
    matches: (p) => p.startsWith("/admin"),
    icon: Icon.Admin,
    group: "system",
  },
];

function initials(email: string): string {
  if (!email) return "??";
  const local = email.split("@")[0];
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const items = useMemo(
    () => NAV.filter((n) => n.id !== "admin" || user?.role === "super_admin"),
    [user?.role]
  );

  const current = items.find((n) => n.matches(location.pathname)) ?? items[0];

  // Close mobile nav on route change
  useEffect(() => {
    setNavOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      const match = items.find((n) => n.kbd.toLowerCase() === key);
      if (match) {
        e.preventDefault();
        navigate(match.to);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, navigate]);

  const workspaceItems = items.filter((n) => n.group === "workspace");
  const systemItems = items.filter((n) => n.group === "system");

  return (
    <div className={`app${navOpen ? " nav-open" : ""}`} data-screen-label={`Chatter / ${current?.label ?? ""}`}>
      <div className="sb-scrim" onClick={() => setNavOpen(false)} />

      <aside className="sb">
        <div className="sb-logo">
          <img src="/logo.png" alt="Chatter" className="sb-logo-img" />
          <div className="sb-name">Chatter</div>
          <div className="sb-ver">v2.6</div>
        </div>

        <div className="sb-section">Workspace</div>
        {workspaceItems.map((n) => {
          const Ico = n.icon;
          const on = current?.id === n.id;
          return (
            <Link
              key={n.id}
              to={n.to}
              className={`sb-item${on ? " on" : ""}`}
              onClick={() => setNavOpen(false)}
            >
              <span className="ico">
                <Ico />
              </span>
              <span className="lbl">{n.label}</span>
              <span className="kbd">{n.kbd}</span>
            </Link>
          );
        })}

        {systemItems.length > 0 && (
          <>
            <div className="sb-section">System</div>
            {systemItems.map((n) => {
              const Ico = n.icon;
              const on = current?.id === n.id;
              return (
                <Link
                  key={n.id}
                  to={n.to}
                  className={`sb-item${on ? " on" : ""}`}
                  onClick={() => setNavOpen(false)}
                >
                  <span className="ico">
                    <Ico />
                  </span>
                  <span className="lbl">{n.label}</span>
                  <span className="kbd">{n.kbd}</span>
                </Link>
              );
            })}
          </>
        )}

        <div className="sb-spacer" />

        {!isTauri() && (
          <Link
            to="/download"
            className="sb-item"
            style={{ marginBottom: 8 }}
            onClick={() => setNavOpen(false)}
            title="Get the desktop app"
          >
            <span className="ico">
              <Icon.Download />
            </span>
            <span className="lbl">Desktop app</span>
          </Link>
        )}

        <div
          className="sb-user"
          onClick={() => setUserMenuOpen((v) => !v)}
          style={{ position: "relative" }}
        >
          <div className="sb-avatar">{initials(user?.email ?? "")}</div>
          <div className="sb-user-meta">
            <div className="e">{user?.email ?? ""}</div>
            <div className="p">
              studio · {user?.role === "super_admin" ? "admin" : "user"}
            </div>
          </div>
          {userMenuOpen && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{
                position: "absolute",
                bottom: "calc(100% + 6px)",
                left: 0,
                right: 0,
                background: "var(--bg-1)",
                border: "1px solid var(--hair)",
                justifyContent: "center",
                color: "var(--ink)",
                zIndex: 20,
              }}
              onClick={(e) => {
                e.stopPropagation();
                void logout();
              }}
            >
              <Icon.Logout /> Sign out
            </button>
          )}
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <button
            type="button"
            className="mobile-nav-btn"
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Open menu"
          >
            <Icon.Menu />
          </button>
          <div className="tb-brand" aria-hidden="true">
            <img src="/logo.png" alt="" className="tb-brand-img" />
            <span className="tb-brand-name">Chatter</span>
          </div>
          <div className="crumbs">
            <span>chatter</span>
            <span className="sep">/</span>
            <b>{current?.label.toLowerCase() ?? ""}</b>
            <span className="sep">·</span>
            <span className="live">live</span>
          </div>
          <div className="tb-spacer" />
          {!isTauri() && (
            <Link to="/download" className="tb-icon-btn" title="Get the desktop app">
              <Icon.Download />
            </Link>
          )}
        </div>

        <Outlet />
      </main>
    </div>
  );
}
